import { useEffect, useRef, useState } from 'react'

import horizontalScrollCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/HorizontalScrollCacheStateManager'

interface UseSwiperNavigationProps {
  childrenCount: number
  spaceBetween?: number | string
  isVertical?: boolean
  afterChange?: (data: { swipeDirection: string | null; activeIndex: number }) => void
  initialSlide?: number
  id: string
  shouldCacheScrollPosition?: boolean
}

export function useSwiperNavigation({
  spaceBetween = 0,
  afterChange,
  isVertical = false,
  initialSlide = 0,
  id,
  shouldCacheScrollPosition = true,
  childrenCount,
}: UseSwiperNavigationProps) {
  const savedScrollLeft = horizontalScrollCacheStateManager.get<number>(id)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const previousScrollLeft = useRef(savedScrollLeft ?? 0)

  const initialSlideRef = useRef(initialSlide)
  const isInitialSlideSet = useRef(false)

  const handleScroll = () => {
    const container = containerRef.current

    if (!container || !isInitialSlideSet.current) return

    const scrollLeft = container.scrollLeft
    const scrollWidth = container.scrollWidth
    const clientWidth = container.clientWidth
    setIsBeginning(scrollLeft === 0)
    // NOTE: Math.ceil to counter the rem to fractional pixel division
    setIsEnd(Math.ceil(scrollLeft + clientWidth) >= scrollWidth)

    let swipeDirection = null
    // eslint-disable-next-line i18next/no-literal-string
    if (previousScrollLeft.current < scrollLeft) swipeDirection = 'next'
    // eslint-disable-next-line i18next/no-literal-string
    else if (previousScrollLeft.current > scrollLeft) swipeDirection = 'prev'

    previousScrollLeft.current = scrollLeft

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const slideWidth =
      (container?.firstChild?.firstChild as HTMLElement)?.clientWidth + parseInt(spaceBetween.toString(), 10)

    const newActiveIndex = Math.round(scrollLeft / slideWidth)
    setActiveIndex(newActiveIndex)

    // Save position on slide change
    horizontalScrollCacheStateManager.set(id, scrollLeft)

    if (afterChange) afterChange({ swipeDirection, activeIndex: newActiveIndex })
  }

  const scrollToNext = () => {
    const container = containerRef.current
    if (container) {
      if (isVertical) {
        container.scrollBy({
          top: container.clientHeight,
          // @i18n-ignore
          behavior: 'smooth',
        })
      } else
        container.scrollBy({
          left: container.clientWidth,
          // @i18n-ignore
          behavior: 'smooth',
        })
    }
  }

  const scrollToPrev = () => {
    const container = containerRef.current
    if (container) {
      if (isVertical) {
        container.scrollBy({
          top: -container.clientHeight,
          // @i18n-ignore
          behavior: 'smooth',
        })
      } else
        container.scrollBy({
          left: -container.clientWidth,
          // @i18n-ignore
          behavior: 'smooth',
        })
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (container) container.addEventListener('scroll', handleScroll)
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Add effect to update isEnd when container is mounted or childrenCount changes
  useEffect(() => {
    const container = containerRef.current
    if (container) {
      const newIsEnd = Math.ceil(container.scrollLeft + container.clientWidth) >= container.scrollWidth
      if (newIsEnd !== isEnd) {
        setIsEnd(newIsEnd)
      }
    }
  }, [childrenCount, isEnd])

  useEffect(() => {
    if (containerRef.current) {
      const itemWidth =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        containerRef.current?.clientWidth + parseInt(spaceBetween.toString(), 10)
      const scrollPosition = itemWidth * initialSlideRef.current

      containerRef.current.scrollTo({
        left: scrollPosition,
        top: 0,
        behavior: 'instant',
      })
      isInitialSlideSet.current = true
    }
  }, [isVertical, spaceBetween])

  useEffect(() => {
    if (!shouldCacheScrollPosition) return
    // Restore scroll position on mount
    const savedScrollLeft = horizontalScrollCacheStateManager.get<number>(id)
    if (containerRef.current) {
      containerRef.current.scrollTo({
        left: savedScrollLeft,
        top: 0,
        // @i18n-ignore
        behavior: 'instant',
      })
    }
  }, [id, shouldCacheScrollPosition])

  return {
    containerRef,
    activeIndex,
    isBeginning,
    isEnd,
    scrollToNext,
    scrollToPrev,
    handleScroll,
  }
}
